import React from 'react';
import { Calendar, ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { useArticles } from '../data/cmsApi';
import { smartImg, smartSrcSet } from '../lib/imgUrl';

const StoriesSection = () => {
  const { t, i18n } = useTranslation();
  const { articles } = useArticles('story', i18n.language);
  // Hide the section entirely if there are no published stories yet
  if (articles === null) {
    return (
      <section className="py-20 bg-gray-50" data-testid="home-stories">
        <div className="flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-gray-400" /></div>
      </section>
    );
  }
  if (articles.length === 0) return null;

  const items = articles.slice(0, 3);

  return (
    <section className="py-20 bg-gray-50" data-testid="home-stories">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">{t('stories.title')}</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{t('stories.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {items.map((story) => (
            <Link key={story.id} to={`/articles/${story.slug}`} className="block">
              <Card className="overflow-hidden border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer group bg-white h-full">
                <div className="relative h-56 overflow-hidden bg-gray-100">
                  {story.image ? (
                    <img loading="lazy" decoding="async"
                      src={smartImg(story.image, 800)}
                      srcSet={smartSrcSet(story.image, [400, 800, 1200]) || undefined}
                      sizes="(max-width: 768px) 100vw, 33vw"
                      width={800}
                      height={500}
                      alt={story.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300" />
                  )}
                </div>
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <Calendar className="w-4 h-4" />
                    <span>{story.date}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-gray-700 transition-colors line-clamp-2">
                    {story.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{story.excerpt}</p>
                  <div className="flex items-center gap-2 text-gray-900 font-medium group-hover:gap-3 transition-all">
                    {t('stories.readStory')}
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StoriesSection;
